// Startbildschirm — die Hof-App als Kachelfeld, wie ein Homescreen.
//
// Bewusst wie iOS gestaltet: Squircle-Kachel (kein Kreis, kein hartes Quadrat),
// Piktogramm mittig, Beschriftung darunter. Die Form ist so vertraut, dass
// niemand erklären muss, was zu tun ist — man tippt drauf.
//
// ══ Wer sieht was ══
// Hier stehen ALLE Seiten, die einen Mitarbeiter angehen — für jeden sichtbar.
// Die Verwaltungs-Seiten (Kasse, Bank, Zeiterfassung, Team) stehen NICHT hier,
// sondern in der Seitenleiste, und die gibt es nur für Admins.
//
// ══ Kachel in die Leiste ziehen ══
// Lang auf eine Kachel drücken → sie hebt ab und hängt am Finger → über die
// untere Leiste ziehen → loslassen. Was in der Leiste hängt, verschwindet vom
// Kachelfeld: Ein Modul an zwei Stellen gleichzeitig wäre nur doppelt, nicht
// nützlicher. Mehr als vier passen nicht in die Leiste — wer ein fünftes
// hineinzieht, muss erst wählen, welches dafür weicht.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MenuButton } from "./Sidebar";
import { useSidebar } from "./SidebarContext";
import { ProfilKnopf } from "./ProfilKnopf";
import { usePinDrag } from "./PinDrag";
import { moduleFuer, modulNachId, MAX_PINS, type Modul } from "@/lib/module-registry";
import { usePins, useSpeicherePins } from "@/lib/pins";
import { useMeineRechte } from "@/lib/rechte";
import { useAuth } from "@/stores/authStore";
import { useUngeleseneNews } from "@/lib/news";
import { useOffeneQs } from "@/lib/qs";
import { useLaufendeSchicht } from "@/lib/zeit";
import { Sheet, cn } from "@/design-system/ui";

/** So lange muss der Finger liegen, bevor die Kachel abhebt. */
const DRUCKDAUER = 450;

export default function HomePage() {
  const { oeffnen } = useSidebar();
  const rolle = useAuth((s) => s.role);
  const istAdmin = rolle === "admin";
  const { data: ungelesen = 0 } = useUngeleseneNews();
  const { data: offeneQs = 0 } = useOffeneQs();
  const { data: schicht } = useLaufendeSchicht();
  const { data: rechte } = useMeineRechte();
  const { data: pins = [] } = usePins();
  const speichern = useSpeicherePins();

  /** Welches Modul will in die volle Leiste? Dann muss erst eins weichen. */
  const [tauschFuer, setTauschFuer] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const badges: Record<string, number | undefined> = {
    news: ungelesen || undefined,
    qs: offeneQs || undefined,
  };
  const hinweise: Record<string, string | undefined> = {
    zeit: schicht ? "läuft" : undefined,
  };

  // Alle Mitarbeiter-Module (die Verwaltung lebt in der Seitenleiste), abzüglich
  // dessen, was schon unten in der Leiste hängt.
  //
  // OHNE Profil: Das eigene Profil hängt oben rechts in JEDER Kopfzeile — von dort
  // ist es aus dem ganzen Modul erreichbar, nicht nur vom Startbildschirm. Eine
  // zweite Kachel für denselben Ort wäre nur eine Kachel mehr zum Übersehen.
  const kacheln = moduleFuer(rolle, rechte?.allowedModules)
    .filter((m) => !m.nurAdmin)
    .filter((m) => m.id !== "profil")
    .filter((m) => !pins.includes(m.id));

  async function anheften(modulId: string) {
    setFehler(null);
    if (pins.includes(modulId)) return;

    if (pins.length >= MAX_PINS) {
      // Voll. Nicht wortlos ablehnen — fragen, wer weichen soll.
      setTauschFuer(modulId);
      return;
    }
    try {
      await speichern.mutateAsync([...pins, modulId]);
    } catch (e) {
      setFehler(lesbar(e));
    }
  }

  /** Der Tausch: `raus` verlässt die Leiste, `tauschFuer` rückt nach. */
  async function tauschen(raus: string) {
    const rein = tauschFuer;
    setTauschFuer(null);
    if (!rein) return;
    setFehler(null);
    try {
      await speichern.mutateAsync([...pins.filter((x) => x !== raus), rein]);
    } catch (e) {
      setFehler(lesbar(e));
    }
  }

  return (
    <div className="relative mx-auto flex h-full max-w-2xl flex-col overflow-y-auto px-6 py-8">
      {/* Kopfleiste: drei Striche links (nur Admin), eigenes Profil rechts.
          Der Theme-Schalter stand hier — er ist ins Profil gewandert, wo er zu
          den anderen persönlichen Einstellungen gehört. Oben rechts hat jetzt
          das, was man dort sucht: sich selbst. */}
      <div className="absolute inset-x-3 top-3 flex items-center justify-between">
        {/* Die Seitenleiste enthält ausschließlich Verwaltungs-Seiten. Für alle
            anderen gäbe es dahinter nichts zu sehen — dann gehört auch der Knopf
            weg, statt ein leeres Menü zu öffnen. */}
        {istAdmin ? <MenuButton onClick={oeffnen} /> : <span />}
        <ProfilKnopf />
      </div>

      <header className="mb-8 mt-6 text-center">
        <img src="/icon-512.png" alt="" className="mx-auto mb-3 h-16 w-16 rounded-2xl shadow-lg" />
        <h1 className="text-xl font-semibold text-ink-50">Hof Sirges</h1>
        <p className="mt-0.5 text-sm text-ink-400">{begruessung()}</p>
      </header>

      {fehler && (
        <p className="mb-4 rounded-lg border border-danger-bg/40 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
          {fehler}
        </p>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:grid-cols-4">
        {kacheln.map((m) => (
          <AppKachel
            key={m.id}
            modul={m}
            badge={badges[m.id]}
            hinweis={hinweise[m.id]}
            onAnheften={() => anheften(m.id)}
          />
        ))}
      </div>

      {kacheln.length === 0 && (
        <p className="mt-6 text-center text-sm text-ink-500">
          Alle Module hängen unten in der Leiste.
        </p>
      )}

      {/* Der Hinweis steht unten und leise: Wer ihn braucht, findet ihn; wer ihn
          kennt, wird nicht jedes Mal belehrt. */}
      {kacheln.length > 0 && (
        <p className="mt-auto pt-8 text-center text-xs text-ink-500">
          Kachel lang drücken und nach unten in die Leiste ziehen
        </p>
      )}

      {/* Leiste voll → wer soll weichen? */}
      <Sheet
        open={!!tauschFuer}
        onClose={() => setTauschFuer(null)}
        title="Die Leiste ist voll"
      >
        <p className="mb-4 text-[15px] text-ink-300">
          Unten passen {MAX_PINS} Module hin.{" "}
          <span className="font-medium text-ink-100">
            {modulNachId(tauschFuer ?? "")?.label}
          </span>{" "}
          kommt hinein — welches soll dafür zurück aufs Kachelfeld?
        </p>
        <ul className="space-y-2">
          {pins.map((id) => {
            const m = modulNachId(id);
            if (!m) return null;
            return (
              <li key={id}>
                <button
                  onClick={() => tauschen(id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border border-ink-700 bg-ink-800/60 px-4 py-4 text-left",
                    "transition-colors duration-fast hover:bg-ink-800",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
                  )}
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-[22.5%] text-white"
                    style={{ backgroundImage: `linear-gradient(160deg, ${m.von}, ${m.bis})` }}
                  >
                    <span className="[&>svg]:h-5 [&>svg]:w-5">{m.icon}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium text-ink-100">
                      {m.label}
                    </span>
                    <span className="block truncate text-sm text-ink-400">{m.hinweis}</span>
                  </span>
                  <span className="shrink-0 text-sm text-ink-400">zurück</span>
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </div>
  );
}

function lesbar(e: unknown) {
  const m = (e as Error).message ?? "";
  return /failed to fetch|load failed|networkerror/i.test(m)
    ? "Kein Netz — die Leiste konnte nicht gespeichert werden."
    : m || "Die Leiste konnte nicht gespeichert werden.";
}

function AppKachel({
  modul,
  badge,
  hinweis,
  onAnheften,
}: {
  modul: Modul;
  badge?: number;
  hinweis?: string;
  onAnheften: () => void;
}) {
  const drag = usePinDrag();
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  /** Nach einem Dauerdruck darf der folgende Klick NICHT navigieren. */
  const schluckeKlick = useRef(false);
  const [haengt, setHaengt] = useState(false);

  const zieheIch = drag.modulId === modul.id;

  const abbrechen = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  };

  return (
    <>
      <Link
        to={modul.to}
        // ══ Ohne das ist das Ziehen tot ══
        // Ein <a> ist im Browser von Haus aus ziehbar. Drückt man darauf und
        // bewegt sich, startet der native HTML5-Drag (das halbtransparente
        // Link-Bild am Cursor) — und der feuert `pointercancel`. Unser eigener
        // Handler bricht daraufhin korrekt ab, und die Geste stirbt exakt bei
        // der ersten Bewegung: Dauerdruck greift, Kachel hebt ab, ein Millimeter
        // Bewegung und alles ist weg.
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => {
          if (schluckeKlick.current) {
            e.preventDefault();
            schluckeKlick.current = false;
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          if (e.pointerType === "mouse" && e.button !== 0) return;
          start.current = { x: e.clientX, y: e.clientY };
          const el = e.currentTarget;
          timer.current = window.setTimeout(() => {
            schluckeKlick.current = true;
            setHaengt(true);
            drag.beginnen(modul.id, e.clientX, e.clientY);
            // Kurz vibrieren, damit man den Druck FÜHLT und nicht rät, ob er saß.
            navigator.vibrate?.(12);
            // Ab jetzt gehören ALLE Pointer-Events dieser Kachel — auch wenn der
            // Finger längst über der Leiste steht. Ohne das verliert man das
            // Element, sobald der Finger es verlässt, und das Ziehen bricht ab.
            try {
              el.setPointerCapture(e.pointerId);
            } catch {
              /* Manche Browser mögen das nach einem Timeout nicht — dann eben ohne. */
            }
          }, DRUCKDAUER);
        }}
        onPointerMove={(e) => {
          if (zieheIch) {
            drag.bewegen(e.clientX, e.clientY);
            return;
          }
          // Wer scrollt, hält nicht gedrückt. 10 px Toleranz gegen Wackeln.
          if (!start.current) return;
          const d = Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y);
          if (d > 10) abbrechen();
        }}
        onPointerUp={() => {
          abbrechen();
          if (!zieheIch) return;
          setHaengt(false);
          const traf = drag.beenden();
          if (traf) onAnheften();
        }}
        onPointerCancel={() => {
          abbrechen();
          if (!zieheIch) return;
          setHaengt(false);
          drag.abbrechen();
        }}
        className={cn(
          "group flex flex-col items-center gap-2 focus-visible:outline-none",
          // Ohne das zeigt iOS beim langen Druck seine eigene Lupe und den
          // Link-Callout — das Ziehen wäre unbenutzbar.
          "select-none [-webkit-touch-callout:none] touch-none",
          haengt && "opacity-30"
        )}
      >
        <div className="relative">
          <div
            className={cn(
              // 22,5 % Radius ist exakt das iOS-Squircle-Maß — deshalb wirkt die
              // Kachel sofort vertraut.
              "grid h-[4.5rem] w-[4.5rem] place-items-center rounded-[22.5%] shadow-lg shadow-black/25",
              "transition-transform duration-fast",
              "group-hover:-translate-y-0.5 group-active:scale-95",
              "group-focus-visible:ring-2 group-focus-visible:ring-gruen-500/70 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-ink-950"
            )}
            style={{ backgroundImage: `linear-gradient(160deg, ${modul.von}, ${modul.bis})` }}
          >
            <div className="text-white [&>svg]:h-[30px] [&>svg]:w-[30px]">{modul.icon}</div>
          </div>

          {badge != null && badge > 0 && (
            <span className="absolute -right-1.5 -top-1.5 grid h-6 min-w-6 place-items-center rounded-full bg-red-500 px-1.5 text-xs font-semibold tabular-nums text-white shadow-md ring-2 ring-ink-950">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </div>

        <div className="text-center">
          <div className="text-[13px] font-medium text-ink-100">{modul.label}</div>
          {hinweis && (
            <div className="mt-0.5 flex items-center justify-center gap-1 text-xs text-gruen-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gruen-400" />
              {hinweis}
            </div>
          )}
        </div>
      </Link>

      {/* Das Geisterbild am Finger. Liegt in einem Portal-freien fixed-Layer und
          ignoriert Zeiger-Ereignisse — sonst würde es sich selbst treffen. */}
      {zieheIch && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[60] -translate-x-1/2 -translate-y-1/2"
          style={{ left: drag.x, top: drag.y }}
        >
          <div
            className={cn(
              "grid h-[4.5rem] w-[4.5rem] place-items-center rounded-[22.5%]",
              "shadow-2xl shadow-black/50 ring-2 ring-white/25",
              "scale-110 transition-transform duration-fast",
              drag.ueberLeiste && "scale-125"
            )}
            style={{ backgroundImage: `linear-gradient(160deg, ${modul.von}, ${modul.bis})` }}
          >
            <div className="text-white [&>svg]:h-[30px] [&>svg]:w-[30px]">{modul.icon}</div>
          </div>
        </div>
      )}
    </>
  );
}

function begruessung() {
  const h = new Date().getHours();
  if (h < 5) return "Gute Nacht";
  if (h < 11) return "Guten Morgen";
  if (h < 18) return "Guten Tag";
  return "Guten Abend";
}

